import { useState } from "react";
import { API_BASE_URL } from "../utils/constants.js";

// Backend constants from vitros-pure/mathlib/AnalyzerSource/appsrvcs/include/CP_ArraySizes.h
//   CP_MAX_NUM_REPS_PER_CAL = 100
// VitrosService.cpp parses aadResponse1/2 row-major filling [level][rep] until input
// is exhausted, so each level slot must contain exactly MAX_REPS_PER_LEVEL values
// (zero-padded). See VitrosService.cpp:400-415.
const MAX_REPS_PER_LEVEL = 100;

/**
 * useMicroSlideApi
 *
 * Hook that wraps the three MicroSlide backend endpoints exposed by
 * vitros-pure/web/server.cpp:
 *   POST /api/v1/microslide/response
 *   POST /api/v1/microslide/calibrate
 *   POST /api/v1/microslide/predict
 *
 * Mirrors the shape of useMicroTipApi so the layout components can be
 * structurally identical to MicroTip.
 */
export default function useMicroSlideApi(parameters = {}, rows = [], extras = {}) {
    const [response, setResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // ------------------------------------------------------------------
    // Helpers
    // ------------------------------------------------------------------
    const num = (v, def = 0) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : def;
    };

    const buildSpline = (s) => {
        if (!s || !Array.isArray(s.nodes) || s.nodes.length === 0) {
            return { sSplineNodeCount: 0, afX: [], afY1: [], afY2: [] };
        }
        return {
            sSplineNodeCount: s.nodes.length,
            afX: s.nodes.map(n => num(n.x)),
            afY1: s.nodes.map(n => num(n.y)),
            afY2: s.nodes.map(n => num(n.y2))
        };
    };

    const buildAssay = () => ({
        eAssayModelType: num(parameters?.eAssayModelType, 1),
        bIncreasingRate: num(parameters?.bIncreasingRate, 1),
        bDrToDtTransformFlag: num(parameters?.bDrToDtTransformFlag),
        eSubDepMethodType: num(parameters?.eSubDepMethodType, 1),
        fInductionTime: num(parameters?.fInductionTime),
        fSDTLimit: num(parameters?.fSDTLimit),
        fSpikeDerivTolerance: num(parameters?.fSpikeDerivTolerance),
        fInsideOutThreshold: num(parameters?.fInsideOutThreshold),
        fSubDepTime1: num(parameters?.fSubDepTime1),
        fSubDepTime2: num(parameters?.fSubDepTime2),
        sMinPointsInWindow: num(parameters?.sMinPointsInWindow),
        fFirstDerivTolerance: num(parameters?.fFirstDerivTolerance),
        eIRAlgorithm: num(parameters?.eIRAlgorithm),
        eRegressionMethodType: num(parameters?.eRegressionMethodType),
        sWindowModulator: num(parameters?.sWindowModulator),
        fWindowLengthMax: num(parameters?.fWindowLengthMax),
        fWindowLengthMin: num(parameters?.fWindowLengthMin),
        fLowRateLongWindowValue: num(parameters?.fLowRateLongWindowValue),
        fHighRateShortWindowValue: num(parameters?.fHighRateShortWindowValue),
        fMWTCurveDampeningConstant: num(parameters?.fMWTCurveDampeningConstant),
        fMWTSpikeTolerance: num(parameters?.fMWTSpikeTolerance),
        fCMSpikeLimitB0: num(parameters?.fCMSpikeLimitB0),
        fCMSpikeLimitB1: num(parameters?.fCMSpikeLimitB1),
        fCMSpikeLimitB2: num(parameters?.fCMSpikeLimitB2),
        dPredictTime1: num(parameters?.dPredictTime1),
        dPredictTime2: num(parameters?.dPredictTime2),
        fADToMVConversionFactor: num(parameters?.fADToMVConversionFactor),
        sTimeTemplateSize: num(parameters?.sTimeTemplateSize),
        fExtrapInductionLimit: num(parameters?.fExtrapInductionLimit),
        fExtrapSlopeLimit: num(parameters?.fExtrapSlopeLimit),
        fReportableRangeLow: num(parameters?.fReportableRangeLow),
        fReportableRangeHigh: num(parameters?.fReportableRangeHigh, 1000),
        fKExponent: num(parameters?.fKExponent),
        bLogarithmicKModel: num(parameters?.bLogarithmicKModel),
        eMonotonicityFlag: num(parameters?.eMonotonicityFlag),

        xPreprocessingSpline: buildSpline(extras?.splines?.preprocessing),
        xG1Spline: buildSpline(extras?.splines?.g1),
        xG2Spline: buildSpline(extras?.splines?.g2),
        xG3Spline: buildSpline(extras?.splines?.g3),
        xG4Spline: buildSpline(extras?.splines?.g4)
    });

    // ------------------------------------------------------------------
    // Payload builders
    // ------------------------------------------------------------------
    const buildResponsePayload = () => {
        const cleanRows = (rows || []).filter(r =>
            r && (r.signal !== "" || r.reference !== "" || r.dark !== "" || r.time !== "")
        );

        return {
            read: {
                iLevel: num(extras?.iLevel),
                iRep: num(extras?.iRep),
                sTotalNumberOfReads: cleanRows.length,
                aiSignalReading: cleanRows.map(r => num(r.signal)),
                aiReferenceReading: cleanRows.map(r => num(r.reference)),
                aiDarkReading: cleanRows.map(r => num(r.dark)),
                adReadTimeInSeconds: cleanRows.map(r => num(r.time)),
                adWhiteCorrFactor: [num(extras?.wcf, 1)],
                adBlackCorrFactor: [num(extras?.bcf)]
            },
            assay: buildAssay()
        };
    };

    // Parse a cell that may contain multiple replicate values.
    // Accepts arrays, comma/space/tab/newline separated strings, or single numbers.
    const parseReps = (v) => {
        if (v === null || v === undefined || v === "") return [];
        if (Array.isArray(v)) return v.map(x => num(x));
        if (typeof v === "string") {
            return v.split(/[\s,]+/).filter(s => s !== "").map(s => num(s));
        }
        return [num(v)];
    };

    const buildCalibratePayload = () => {
        // rows: [{ sav, resp1, resp2 }]
        // One row per calibration level, mirroring the old
        // MSDualWavelengthLevel { SAV, Resp1[], Resp2[] } structure.
        const cleanRows = (rows || []).filter(r =>
            r && (r.resp1 !== "" && r.resp1 !== undefined)
        );

        const aadResponse1 = [];
        const aadResponse2 = [];
        const asNumberCalReps = [];
        const afSAV = [];

        cleanRows.forEach(r => {
            const r1 = parseReps(r.resp1);
            const r2 = parseReps(r.resp2);
            // Old DMController guards level.Resp1.Count == level.Resp2.Count.
            // Use whichever count is non-zero; pad missing wavelength with zeros.
            const repCount = Math.min(
                Math.max(r1.length, r2.length),
                MAX_REPS_PER_LEVEL
            );

            const padded1 = new Array(MAX_REPS_PER_LEVEL).fill(0);
            const padded2 = new Array(MAX_REPS_PER_LEVEL).fill(0);
            for (let i = 0; i < repCount; i++) {
                padded1[i] = r1[i] ?? 0;
                padded2[i] = r2[i] ?? 0;
            }
            aadResponse1.push(...padded1);
            aadResponse2.push(...padded2);
            asNumberCalReps.push(repCount);
            afSAV.push(num(r.sav));
        });

        return {
            ...buildAssay(),
            sNumberCalLevels: cleanRows.length,
            asNumberCalReps,
            afSAV,
            aadResponse1,
            aadResponse2
        };
    };

    // Build payload for a single sample point. Predict is called once per row
    // by callPredict (matches old DMController which loops predictRequest.Points).
    const buildPredictPayloadFor = (row) => ({
        input: {
            dResponse1: num(row?.response1),
            dResponse2: num(row?.response2),
            dFirstDr: 0,
            dSubDepCheckValue: 0,
            dSubDepDeltaCheckValue: 0,
            dBlankConcentration: 0
        },
        assay: {
            ...buildAssay(),
            fCalInterceptA0: num(parameters?.fCalInterceptA0),
            fCalSlopeA1: num(parameters?.fCalSlopeA1, 1),
            fCalCurvatureA2: num(parameters?.fCalCurvatureA2),
            fCalBlankCorrectionFactorA3: num(parameters?.fCalBlankCorrectionFactorA3)
        }
    });

    // ------------------------------------------------------------------
    // Calls
    // ------------------------------------------------------------------
    const call = async (endpoint, payload) => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(API_BASE_URL + "api/v1/microslide/" + endpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            setResponse(data);
            return data;
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    };

    const callResponse = () => call("response", buildResponsePayload());
    const callCalibrate = () => call("calibrate", buildCalibratePayload());

    // Predict batches client-side, one POST per sample row (matches old
    // DMController.PredictDualWavelength which loops predictRequest.Points).
    const callPredict = async () => {
        const cleanRows = (rows || []).filter(r =>
            r && (r.response1 !== "" && r.response1 !== undefined)
        );
        if (cleanRows.length === 0) {
            setError("No sample rows with Response 1 to predict");
            return null;
        }

        setLoading(true);
        setError(null);
        try {
            const predictions = [];
            for (const row of cleanRows) {
                const res = await fetch(API_BASE_URL + "api/v1/microslide/predict", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(buildPredictPayloadFor(row))
                });
                const data = await res.json();
                predictions.push({
                    sampleId: row.sampleId ?? "",
                    response1: row.response1,
                    response2: row.response2,
                    concentration: data?.dConcentration ?? null,
                    conditionCode: data?.zConditionCode ?? "",
                    error: data?.zError ?? ""
                });
            }
            const result = { predictions };
            setResponse(result);
            return result;
        } catch (err) {
            setError(err.message);
            return null;
        } finally {
            setLoading(false);
        }
    };

    return { callResponse, callCalibrate, callPredict, response, loading, error };
}
